import { StateCreator } from 'zustand';
import { apiClient } from '@core/api/ApiClientFactory';
import type { OperationMode } from '@/services';
import { ShopStatusResponse } from '@features/shopautomation/ShopAutomationShops.tsx';
import { TagState } from '@/components/shared/Tags.tsx';
import { buildOperationModeStateMap } from '@core/hooks/useModeTagState.ts';

interface Organisation {
    id: string;
    organisationId: string;
    name: string;
    fastLanerOrgId: string;
}

export interface ShopId {
    id: string;
    name: string;
    isProduction: boolean;
    fastLanerGroupIds: string[];
    organisationId: string;
}

export interface ShopStatusProps {
    shopIdName?: ShopId;
    heading?: string;
    className?: string;
}

export interface ShopStatusItem {
    title?: string;
    id: string;
    name: string;
    isProduction?: boolean;
    status: any;
    unhealthy: any[];
    type: string;
    shopId: string;
    children?: ShopStatusItem[];
}

export interface Shop {
    id: string;
    name: string;
    isProduction: boolean;
    fastLanerGroupIds?: string[];
    //fastLanerGroupName?: string;
    status?: {
        healthStatusType: string;
        changeDate: string | null;
        isOnline: boolean;
    };
    unhealthy?: string[];
    gateways?: Gateway[];
}

interface Gateway {
    id: string;
    deviceId: string;
    status: any;
    unhealthy: string[];
    modules: Module[];
}

interface Module {
    discriminator: string;
    id: string;
    moduleId: string;
    status: any;
    unhealthy: string[];
    [key: string]: any; // For specific module properties
}

export interface Product {
    id: string | null;
    articleNumber: string | null;
    name: string | null;
    productTypeId: string | null;
    disabled: boolean;
    productType?: ProductType;
}

export interface ProductType {
    id: string | null;
    typeName: string;
}

interface ApiState {
    shopIds: Array<{ id: string; name: string; isProduction: boolean }>;
    componentIds: Array<{
        id: string;
        shopId: string;
        discriminator: string;
        type: string;
        disabled: boolean;
        deleteDate: string | null;
    }>;
    shopsData: Shop[];
    isLoading: boolean;
    error: string | null;
}

export interface ComponentId {
    id: string;
    deviceId: string;
    shopId: string;
    discriminator: string;
    type: string;
    disabled: boolean;
    deleteDate?: string | null;
    name?: string;
}

export interface ModuleId {
    id: string;
    shopId: string;
    disabled: boolean;
    deleteDate?: string | null;
    deviceId: string;
}

interface ApiInfo {
    version: string;
    authTenant: string;
    authPolicy: string;
    authClientId: string;
    aiKey: string;
}

interface GatewayId {
    id: string;
    shopId: string;
    disabled: boolean;
    deleteDate?: string | null;
    deviceId?: string;
}

interface SnmpDeviceType {
    id: string;
    name: string;
}

export interface ApiSlice {
    organisation: Organisation | null;
    shopIds: ShopId[];
    shopData: ShopStatusResponse[];
    componentIds: ComponentId[];
    gatewayIds: GatewayId[];
    trolleyShelterIds: ModuleId[];
    zoneTrackerIds: ModuleId[];
    lineTrackerIds: ModuleId[];
    shopAutomationIds: ModuleId[];
    smartShelvesIds: ModuleId[];
    operationModes: OperationMode[];
    operationModeStateMap: Record<string, TagState>;
    snmpDeviceTypes: SnmpDeviceType[];
    rfidTagProducts: Product[];
    apiInfo: ApiInfo | null;
    isLoading: boolean;
    error: string | null;

    // Actions
    loadOrganisation: () => Promise<void>;
    loadShopIds: () => Promise<void>;
    loadShopStatus: () => Promise<void>;
    loadComponentIds: () => Promise<void>;
    loadGatewayIds: () => Promise<void>;
    loadTrolleyShelterIds: () => Promise<void>;
    loadZoneTrackerIds: () => Promise<void>;
    loadLineTrackerIds: () => Promise<void>;
    loadShopAutomationIds: () => Promise<void>;
    loadSmartShelvesIds: () => Promise<void>;
    loadOperationModes: () => Promise<void>;
    loadSnmpDeviceTypes: () => Promise<void>;
    loadRfidTagProducts: () => Promise<void>;
    loadApiInfo: () => Promise<void>;

    // Helper methods
    hasComponent: (component: string) => boolean;
    hasModule: (module: string | string[]) => boolean;
    getModulesForShop: (moduleType: string, shopId: string) => ComponentId[];
    clearApiData: () => void;
}

export const createApiSlice: StateCreator<ApiSlice> = (set, get) => ({
    organisation: null,
    shopIds: [],
    shopData: [],
    componentIds: [],
    gatewayIds: [],
    trolleyShelterIds: [],
    zoneTrackerIds: [],
    lineTrackerIds: [],
    shopAutomationIds: [],
    smartShelvesIds: [],
    operationModes: [],
    operationModeStateMap: {},
    snmpDeviceTypes: [],
    rfidTagProducts: [],
    apiInfo: null,
    isLoading: false,
    error: null,

    clearApiData: () => {
        set({
            organisation: null,
            shopIds: [],
            shopData: [],
            componentIds: [],
            gatewayIds: [],
            trolleyShelterIds: [],
            zoneTrackerIds: [],
            lineTrackerIds: [],
            shopAutomationIds: [],
            smartShelvesIds: [],
            operationModes: [],
            operationModeStateMap: {},
            snmpDeviceTypes: [],
            rfidTagProducts: [],
            apiInfo: null,
            error: null
        });
    },

    loadOrganisation: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get<Organisation>('organisation');
            set({ organisation: response.data, isLoading: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load organisation';
            console.error('[ApiSlice] Failed to load organisation:', error);
            set({ error: errorMessage, isLoading: false });
        }
    },

    loadShopIds: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get<ShopId[]>('shop/shopIds');
            set({ shopIds: response.data.sort((a, b) => a.name.localeCompare(b.name)), isLoading: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load shop IDs';
            console.error('[ApiSlice] Failed to load shop IDs:', error);
            set({ error: errorMessage, isLoading: false });
            throw error;
        }
    },

    loadShopStatus: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get<ShopStatusResponse[]>('shop/status');
            console.log('[ApiSlice] Load Shop Status:', response.data);
            set({ shopData: response.data, isLoading: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load shop status';
            console.error('[ApiSlice] Failed to load shop status:', error);
            set({ error: errorMessage, isLoading: false });
        }
    },

    loadComponentIds: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get<ComponentId[]>('component/componentIds');
            set({ componentIds: response.data, isLoading: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load component IDs';
            console.error('[ApiSlice] Failed to load component IDs:', error);
            set({ error: errorMessage, isLoading: false });
        }
    },

    loadGatewayIds: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get<ModuleId[]>('gateway/gatewayIds');
            set({ gatewayIds: response.data, isLoading: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load gateway IDs';
            console.error('[ApiSlice] Failed to load gateway IDs:', error);
            set({ error: errorMessage, isLoading: false });
        }
    },

    loadTrolleyShelterIds: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get<ModuleId[]>('trolleyshelter/trolleyShelterIds');
            set({ trolleyShelterIds: response.data, isLoading: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load trolley shelter IDs';
            console.error('[ApiSlice] Failed to load trolley shelter IDs:', error);
            set({ error: errorMessage, isLoading: false });
        }
    },

    loadZoneTrackerIds: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get<ModuleId[]>('zoneTracker/zoneTrackerIds');
            set({ zoneTrackerIds: response.data, isLoading: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load zone tracker IDs';
            console.error('[ApiSlice] Failed to load zone tracker IDs:', error);
            set({ error: errorMessage, isLoading: false });
        }
    },

    loadLineTrackerIds: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get<ModuleId[]>('lineTracker/lineTrackerIds');
            set({ lineTrackerIds: response.data, isLoading: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load line tracker IDs';
            console.error('[ApiSlice] Failed to load line tracker IDs:', error);
            set({ error: errorMessage, isLoading: false });
        }
    },

    loadShopAutomationIds: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get<ModuleId[]>('shopAutomation/shopAutomationIds');
            set({ shopAutomationIds: response.data, isLoading: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load shop automation IDs';
            console.error('[ApiSlice] Failed to load shop automation IDs:', error);
            set({ error: errorMessage, isLoading: false });
        }
    },

    loadSmartShelvesIds: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get<ModuleId[]>('smartShelf/smartShelvesIds');
            set({ smartShelvesIds: response.data, isLoading: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load smart shelves IDs';
            console.error('[ApiSlice] Failed to load smart shelves IDs:', error);
            set({ error: errorMessage, isLoading: false });
        }
    },

    loadOperationModes: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get<OperationMode[]>('shopAutomation/operationModes');
            set({ operationModes: response.data, operationModeStateMap: buildOperationModeStateMap(response.data), isLoading: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load operation modes';
            console.error('[ApiSlice] Failed to load operation modes:', error);
            set({ error: errorMessage, isLoading: false });
        }
    },

    loadSnmpDeviceTypes: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get<SnmpDeviceType[]>('snmpDeviceType');
            set({ snmpDeviceTypes: response.data, isLoading: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load SNMP device types';
            console.error('[ApiSlice] Failed to load SNMP device types:', error);
            set({ error: errorMessage, isLoading: false });
        }
    },

    loadRfidTagProducts: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get<Product[]>('rfidTagProduct');
            set({ rfidTagProducts: response.data, isLoading: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load RFID tag products';
            console.error('[ApiSlice] Failed to load RFID tag products:', error);
            set({ error: errorMessage, isLoading: false });
        }
    },

    loadApiInfo: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get<ApiInfo>('health/info');
            set({ apiInfo: response.data, isLoading: false });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load API info';
            console.error('[ApiSlice] Failed to load API info:', error);
            set({ error: errorMessage, isLoading: false });
        }
    },

    hasComponent: (component: string) => {
        if (!component || component.trim() === '') {
            return false;
        }

        const { componentIds } = get();
        const componentLower = component.toLowerCase();

        return componentIds.some((comp) => {
            if (comp == null || comp.discriminator == null) {
                return false;
            }

            return comp.discriminator.toLowerCase() === componentLower;
        });
    },

    hasModule: (module: string | string[]) => {
        const modules = Array.isArray(module) ? module : [module];

        return modules.some((mod) => {
            if (!mod || mod.trim() === '') {
                return false;
            }

            const state = get();
            const moduleKey = `${mod}Ids` as keyof ApiSlice;
            const moduleIds = state[moduleKey];

            if (!Array.isArray(moduleIds)) {
                return false;
            }

            return moduleIds.some((item: any) => item && !item.disabled && !item.deleteDate);
        });
    },

    getModulesForShop: (moduleType: string, shopId: string) => {
        const { componentIds } = get();
        const moduleTypeLower = moduleType.toLowerCase();

        return componentIds.filter(
            (component) => component.shopId === shopId && component.discriminator.toLowerCase() === moduleTypeLower
        );
    }
});
